// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import {
  useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react'
import { ArrowDown, ArrowUp, GripVertical, RotateCcw, Search, SlidersHorizontal } from 'lucide-react'
import { useTableFunnels, type FunnelColumn } from './common/TableFunnels'

/**
 * DataGrid — a reusable SharePoint/Excel-style list table. Columns are movable (drag the header),
 * resizable (drag the right edge), sortable (click the header), and show/hide-able (Columns menu).
 * A quick-filter box searches all visible columns. Layout (order, widths, hidden, sort) persists per
 * `storageKey` in localStorage, so each view remembers how the user arranged it — like a SharePoint view.
 *
 * Adopt on any list: pass `columns` (key, header, optional render/value/width/align) + `rows`.
 */
export interface DataGridColumn<T> {
  key: string
  header: string
  width?: number
  minWidth?: number
  align?: 'left' | 'right' | 'center'
  /** custom cell renderer; falls back to `value(row)` then `row[key]` */
  render?: (row: T) => ReactNode
  /** scalar used for sorting + filtering + default render */
  value?: (row: T) => string | number | null | undefined
  /** hidden by default */
  hidden?: boolean
  /** exclude from the quick-filter match */
  noFilter?: boolean
}

interface Persisted {
  order?: string[]
  widths?: Record<string, number>
  hidden?: Record<string, boolean>
  sort?: { key: string; dir: 'asc' | 'desc' } | null
}

const LS_PREFIX = 'datagrid:'

export function DataGrid<T>({
  rows, columns, storageKey, getRowKey, emptyText = 'No rows.', dense = false,
}: {
  rows: T[]
  columns: DataGridColumn<T>[]
  storageKey: string
  getRowKey: (row: T, i: number) => string
  emptyText?: string
  dense?: boolean
}) {
  const colByKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns])
  const defaultOrder = useMemo(() => columns.map(c => c.key), [columns])

  const [persist, setPersist] = useState<Persisted>(() => {
    try {
      const raw = localStorage.getItem(LS_PREFIX + storageKey)
      if (raw) return JSON.parse(raw) as Persisted
    } catch { /* ignore */ }
    return {}
  })
  // save on change
  useEffect(() => {
    try { localStorage.setItem(LS_PREFIX + storageKey, JSON.stringify(persist)) } catch { /* ignore */ }
  }, [persist, storageKey])

  // effective column order = persisted order (filtered to known keys) + any new columns appended
  const order = useMemo(() => {
    const known = new Set(defaultOrder)
    const saved = (persist.order ?? []).filter(k => known.has(k))
    const rest = defaultOrder.filter(k => !saved.includes(k))
    return [...saved, ...rest]
  }, [persist.order, defaultOrder])

  const hidden = persist.hidden ?? {}
  const widths = persist.widths ?? {}
  const sort = persist.sort ?? null

  const visibleCols = useMemo(
    () => order.map(k => colByKey.get(k)!).filter(c => c && !(hidden[c.key] ?? c.hidden)),
    [order, colByKey, hidden],
  )

  const [filter, setFilter] = useState('')
  const [colMenu, setColMenu] = useState(false)

  const cellValue = useCallback((c: DataGridColumn<T>, row: T): unknown => {
    if (c.value) return c.value(row)
    return (row as Record<string, unknown>)[c.key]
  }, [])

  // D-UI-2: a funnel on every labelled column, applied to the whole row set BEFORE the quick
  // filter and the sort. The funnel columns are keyed on the keys + headers rather than on the
  // `columns` array, because a caller may build that array inline on every render; the accessor
  // reads the current column through a ref, so it never goes stale.
  const colsRef = useRef(colByKey)
  colsRef.current = colByKey
  const funnelSig = columns.filter(c => c.header).map(c => `${c.key}\u0001${c.header}`).join('\u0002')
  const funnelCols = useMemo<FunnelColumn<T>[]>(() => (funnelSig ? funnelSig.split('\u0002') : []).map(s => {
    const [key, label] = s.split('\u0001')
    return {
      key, label,
      value: (row: T) => {
        const c = colsRef.current.get(key)
        if (!c) return null
        // A renderer that prints plain text IS what the cell shows; one that returns markup
        // falls back to the column's scalar.
        if (c.render) {
          const shown = c.render(row)
          if (typeof shown === 'string' || typeof shown === 'number') return shown
        }
        return cellValue(c, row)
      },
    }
  }), [funnelSig, cellValue])
  const fx = useTableFunnels(rows, funnelCols)
  const funnelRows = fx.rows

  // filter + sort
  const view = useMemo(() => {
    let out = funnelRows
    const q = filter.trim().toLowerCase()
    if (q) {
      const cols = visibleCols.filter(c => !c.noFilter)
      out = out.filter(row => cols.some(c => {
        const v = cellValue(c, row)
        return v != null && String(v).toLowerCase().includes(q)
      }))
    }
    if (sort) {
      const c = colByKey.get(sort.key)
      if (c) {
        const dir = sort.dir === 'asc' ? 1 : -1
        out = [...out].sort((a, b) => {
          const va = cellValue(c, a), vb = cellValue(c, b)
          if (va == null && vb == null) return 0
          if (va == null) return 1
          if (vb == null) return -1
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
          return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
        })
      }
    }
    return out
  }, [funnelRows, filter, sort, visibleCols, colByKey, cellValue])

  // --- column reorder (drag header) ---
  const dragKey = useRef<string | null>(null)
  const onDrop = (targetKey: string) => {
    const from = dragKey.current
    dragKey.current = null
    if (!from || from === targetKey) return
    const next = order.filter(k => k !== from)
    const idx = next.indexOf(targetKey)
    next.splice(idx, 0, from)
    setPersist(p => ({ ...p, order: next }))
  }

  // --- column resize (drag right edge) ---
  const resizing = useRef<{ key: string; startX: number; startW: number } | null>(null)
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const r = resizing.current
      if (!r) return
      const c = colByKey.get(r.key)
      const min = c?.minWidth ?? 64
      const w = Math.max(min, r.startW + (e.clientX - r.startX))
      setPersist(p => ({ ...p, widths: { ...(p.widths ?? {}), [r.key]: w } }))
    }
    const up = () => { resizing.current = null; document.body.style.cursor = '' }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up) }
  }, [colByKey])

  const toggleSort = (key: string) => setPersist(p => {
    const cur = p.sort
    if (!cur || cur.key !== key) return { ...p, sort: { key, dir: 'asc' } }
    if (cur.dir === 'asc') return { ...p, sort: { key, dir: 'desc' } }
    return { ...p, sort: null }
  })

  const reset = () => setPersist({})

  const widthOf = (c: DataGridColumn<T>) => widths[c.key] ?? c.width ?? 150
  const thBase: CSSProperties = {
    position: 'relative', textAlign: 'left', fontWeight: 700, color: '#4a5160',
    background: '#f7f8fa', borderBottom: '1px solid #e3e6ea', borderRight: '1px solid #e7e9ee',
    padding: dense ? '6px 10px' : '9px 12px', whiteSpace: 'nowrap', userSelect: 'none',
  }

  return (
    <div>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9aa1ad' }} />
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter…"
            style={{ width: '100%', padding: '6px 10px 6px 28px', border: '1px solid #d7dae0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setColMenu(v => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d7dae0', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>
            <SlidersHorizontal size={14} /> Columns
          </button>
          {colMenu && (
            <>
              <div onClick={() => setColMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
              <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 31, background: '#fff', border: '1px solid #e3e6ea', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.12)', padding: 8, minWidth: 200 }}>
                {order.map(k => {
                  const c = colByKey.get(k)!
                  const isHidden = hidden[k] ?? c.hidden ?? false
                  return (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, cursor: 'pointer', borderRadius: 6 }}>
                      <input type="checkbox" checked={!isHidden}
                        onChange={() => setPersist(p => ({ ...p, hidden: { ...(p.hidden ?? {}), [k]: !isHidden } }))}
                      />
                      {c.header}
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>
        <button onClick={reset} title="Reset columns" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d7dae0', background: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#5b6270' }}>
          <RotateCcw size={13} /> Reset
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8a91a0' }}>{view.length} of {rows.length}</span>
      </div>

      {fx.chips}

      {/* grid — horizontal scroll wrapper so wide tables never break the page */}
      <div style={{ overflowX: 'auto', border: '1px solid #eceef1', borderRadius: 12 }}>
        <table {...fx.tableProps} style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: '100%', tableLayout: 'fixed' }}>
          <colgroup>{visibleCols.map(c => <col key={c.key} style={{ width: widthOf(c) }} />)}</colgroup>
          <thead>
            <tr>
              {visibleCols.map(c => {
                const active = sort?.key === c.key
                return (
                  <th key={c.key} style={{ ...thBase, textAlign: c.align ?? 'left' }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => onDrop(c.key)}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      {/* grip = reorder (only this is draggable, so the resize edge never starts a move) */}
                      <span title="Drag to reorder"
                        draggable
                        onDragStart={() => { dragKey.current = c.key }}
                        onDragEnd={() => { dragKey.current = null }}
                        style={{ display: 'inline-flex', cursor: 'grab', flex: '0 0 auto' }}>
                        <GripVertical size={12} style={{ color: '#c3c8d0' }} />
                      </span>
                      <span onClick={() => toggleSort(c.key)} title="Click to sort"
                        style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.header}</span>
                      {active && (sort!.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                      {/* funnel — a sibling of the grip and the label, so it is neither draggable nor
                          inside the sort click target; a drag can never start from it. */}
                      {c.header && (
                        <span draggable={false} onDragStart={e => { e.preventDefault(); e.stopPropagation() }}
                          style={{ display: 'inline-flex', flex: '0 0 auto' }}>
                          {fx.funnel(c.key)}
                        </span>
                      )}
                    </span>
                    {/* resize handle — drag the right edge to size the column, Excel-style */}
                    <span title="Drag to resize"
                      onMouseDown={e => { resizing.current = { key: c.key, startX: e.clientX, startW: widthOf(c) }; document.body.style.cursor = 'col-resize'; e.preventDefault(); e.stopPropagation() }}
                      onClick={e => e.stopPropagation()}
                      style={{ position: 'absolute', top: 0, right: -3, width: 10, height: '100%', cursor: 'col-resize', zIndex: 2 }} />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {view.length === 0 ? (
              <tr><td colSpan={visibleCols.length} style={{ padding: 24, textAlign: 'center', color: '#8a91a0' }}>{emptyText}</td></tr>
            ) : view.map((row, i) => (
              <tr key={getRowKey(row, i)} style={{ background: i % 2 ? '#fcfcfd' : '#fff' }}>
                {visibleCols.map(c => (
                  <td key={c.key} style={{
                    padding: dense ? '5px 10px' : '8px 12px', textAlign: c.align ?? 'left', verticalAlign: 'top',
                    borderBottom: '1px solid #f0f1f3', borderRight: '1px solid #f2f3f5',
                    whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere',
                  }}>
                    {c.render ? c.render(row) : String(cellValue(c, row) ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DataGrid
